import React from "react";
import { RouteComponentProps } from "react-router-dom";
import "./Course.css";
import "../../App.css";
import HashLoader from "react-spinners/HashLoader";
import {
  dateToFullStr,
  firebaseDateToDateObj,
  priceToStr,
} from "../../functions/utils";
import { fetchRetry } from "../../functions/request";
import { MainContext } from "../../contexts/MainContext";
import Title from "../../components/Title";
import SingleCourseElementClub from "../../components/SingleCourseElementClub";
import BlockCourseElementClub from "../../components/BlockCourseElementClub";
import InfinityCourseElementClub from "../../components/InfinityCourseElementClub";
import SearchInput from "../../components/SearchInput";
import InfiniteScroll from "react-infinite-scroll-component";
import CourseElementSkeleton from "../../components/CourseElementSkeleton";
import debounce from "lodash.debounce";
import { apiRequest } from "../../functions/requestMulti";
import qs from "qs";
import CourseViewSwitch from "../../components/CourseViewSwitch";
import Timetable, { TimetableSnapshot } from "../../components/Timetable";
import { isTimetableEnabled } from "../../functions/timetable";
import {
  ListCacheEntry,
  getScrollContainer,
  restoreListState,
  restoreScrollPosition,
  saveListState,
} from "../../functions/listStateCache";

type Props = {};
type ComposedProps = Props &
  RouteComponentProps<{
    clubId: string;
  }>;

export default class Course extends React.Component<
  ComposedProps,
  {
    clubId: string;
    coursesLoaded: boolean;
    courses: Array<any>;
    showJoinModal: boolean;
    joinModalLoading: boolean;
    joinModalMsg: null | string;
    joinModalType: "error" | "success";
    showRequestedModal: boolean;
    requestedModalLoading: boolean;
    requestedModalMsg: null | string;
    requestedModalType: "error" | "success";
    courseSearch: string;
    batchSize: number;
    page: number;
    hasMore: boolean;
    initialLoad: boolean;
    // 1 = Liste, 2 = Stundenplan
    view: number;
    timetableState: TimetableSnapshot | null;
  }
> {
  static contextType = MainContext;

  private cacheKey: string;
  private restored: ListCacheEntry | null;
  private leavingToDetail = false;
  private cancelScroll: (() => void) | null = null;
  private mounted = false;
  private restoringPosition = false;
  private rootRef = React.createRef<HTMLDivElement>();
  private timetableRef = React.createRef<Timetable>();
  // released, or previewed with ?calendar=true
  private showTimetable = isTimetableEnabled();

  constructor(props: ComposedProps) {
    super(props);
    this.cacheKey = props.match.url;
    this.restored = restoreListState(this.cacheKey);
    this.state = {
      clubId: this.props.match.params.clubId,
      coursesLoaded: false,
      courses: [],
      showJoinModal: false,
      joinModalLoading: false,
      joinModalMsg: null,
      joinModalType: "error",
      showRequestedModal: false,
      requestedModalLoading: false,
      requestedModalMsg: null,
      requestedModalType: "error",
      courseSearch: "",
      batchSize: 10,
      page: 0,
      hasMore: true,
      initialLoad: false,
      timetableState: null,
      // Returning from a course's detail page: render every page the user had
      // already loaded, plus their search.
      ...(this.restored ? this.restored.state : {}),
      view: this.initialView(),
    };
  }

  componentDidMount = () => {
    this.mounted = true;
    if (this.showTimetable) this.syncViewToUrl(this.state.view);
    if (this.restored) {
      this.restorePosition();
      // Silent refresh of the whole loaded window - coursesLoaded stays true,
      // so no skeletons and no scroll jump.
      this.revalidateWindow();
      return;
    }
    this.requestCourses();
  };

  componentWillUnmount = () => {
    this.mounted = false;
    if (this.cancelScroll) this.cancelScroll();
    this.debounceRequest.cancel();
    if (this.leavingToDetail) {
      saveListState(
        this.cacheKey,
        {
          courses: this.state.courses,
          page: this.state.page,
          hasMore: this.state.hasMore,
          courseSearch: this.state.courseSearch,
          coursesLoaded: true,
          initialLoad: true,
          view: this.state.view,
          timetableState: this.currentTimetableState(),
        },
        this.rootRef.current
      );
    }
  };

  /**
   * A back link may carry a `#courseId`. When it names a card that is actually
   * on screen, honour it - it is more precise than the saved offset. Otherwise
   * fall back to the saved scroll position.
   */
  restorePosition = () => {
    this.restoringPosition = true;
    const settled = () => {
      this.restoringPosition = false;
    };
    const hashId = window.location.hash.slice(1);
    const target = hashId ? document.getElementById(hashId) : null;
    if (target) {
      this.scrollToCard(target);
      requestAnimationFrame(settled);
      return;
    }
    if (!this.restored) {
      settled();
      return;
    }
    this.cancelScroll = restoreScrollPosition(
      this.restored,
      this.rootRef.current,
      30, // cards grow as their images decode
      settled
    );
  };

  scrollToCard = (target: HTMLElement) => {
    const container = getScrollContainer(this.rootRef.current);
    if (!container) {
      target.scrollIntoView();
      return;
    }
    // `.br-inner-container` has `scroll-behavior: smooth`, which would animate
    // the jump; an inline style beats the stylesheet rule.
    const prev = container.style.scrollBehavior;
    container.style.scrollBehavior = "auto";
    target.scrollIntoView();
    container.style.scrollBehavior = prev;
  };

  /**
   * Re-reads every page the user had already loaded and replaces the list, so
   * changes made elsewhere show up after a back-navigation.
   *
   * One request per page rather than one big one: the v2 API caps hitsPerPage
   * at 20 (Zod `.max(20)` in elementSevice), and exceeding it fails the parse
   * and returns a 500.
   */
  revalidateWindow = async () => {
    const pages = [];
    for (let i = 0; i <= this.state.page; i++) pages.push(i);
    const results = await Promise.all(
      pages.map((page) => this.fetchCoursePage(page))
    ).catch(this.handleError);
    if (!results || !this.mounted) return;

    const courses: Array<any> = [];
    const seen: { [id: string]: boolean } = {};
    results.forEach((pageCourses: any) => {
      (pageCourses || []).forEach((course: any) => {
        // Offset paging can shift when items are added or removed between
        // visits, so the same course can come back on two pages.
        if (course && !seen[course.id]) {
          seen[course.id] = true;
          courses.push(course);
        }
      });
    });

    const lastPage = results[results.length - 1];
    this.setState({
      courses,
      hasMore: !!lastPage && lastPage.length >= this.state.batchSize,
    });
  };

  fetchCoursePage = async (page: number) => {
    const { data } = await apiRequest(
      "api/elements/club",
      "GET",
      this.courseParams(page)
    );
    return data.success ? data.data : null;
  };

  courseParams = (page: number) => ({
    targetUserId: this.context.selectedUserId,
    clubId: this.state.clubId,
    query: this.state.courseSearch,
    page,
    hitsPerPage: this.state.batchSize,
    showDeleted: false,
    showPast: false,
  });

  requestCourses = () => {
    apiRequest("api/elements/club", "GET", this.courseParams(this.state.page))
      .then(this.handleCoursesSuccess)
      .catch(this.handleError);
    // fetchRetry(
    //   "getCoursesOfClubAlgoliaF",
    //   {
    //     clubId: this.state.clubId,
    //     query: this.state.courseSearch,
    //     page: this.state.page,
    //     hitsPerPage: this.state.batchSize,
    //     targetUserId: this.context.selectedUserId,
    //   },
    //   1,
    //   5
    // )
    //   .then(this.handleCoursesSuccess)
    //   .catch(this.handleError);
  };

  handleError = (err: any) => {
    console.error(err);
  };

  handleCoursesSuccess = ({ data }: any) => {
    if (!this.mounted) return;
    this.setState({ coursesLoaded: true });
    if (data.success) {
      const courses = this.state.courses.concat(data.data);
      this.setState({
        courses,
        hasMore: data.data.length >= this.state.batchSize,
      });
    }
    // Guarded by initialLoad so "Mehr laden" cannot yank the user back to the
    // highlighted card on every page. `location.hash` rather than slicing the
    // full href - the old version handed getElementById the entire URL
    // whenever there was no "#" at all.
    if (this.state.initialLoad) return;
    const highlighedCourseId = window.location.hash.slice(1);
    const element = highlighedCourseId
      ? document.getElementById(highlighedCourseId)
      : null;
    if (element) this.scrollToCard(element);
    this.setState({ initialLoad: true });
  };

  handleChangeCourseSearch = (val: string) => {
    this.setState({ courseSearch: val }, this.debounceRequest);
  };

  debounceRequest = debounce(() => {
    this.newRequest();
  }, 500);

  newRequest = () => {
    this.setState(
      { courses: [], page: 0, coursesLoaded: false, hasMore: true },
      this.requestCourses
    );
  };

  getNextPage = () => {
    // The restore pass writes scrollTop programmatically, which fires scroll
    // events. Without this guard InfiniteScroll would append a page - and
    // flash skeletons - before the restored list has settled.
    if (this.restoringPosition) return;
    this.setState(
      { page: this.state.page + 1, coursesLoaded: false },
      this.requestCourses
    );
  };

  /**
   * The Stundenplan is the default, the list is `?view=liste`. An explicit
   * `?view=` wins (old `?view=stundenplan` links keep working); without one -
   * the course page's own "Zurück zur Kursauswahl" link drops the query - the
   * view restored from the list cache does.
   */
  initialView = (): number => {
    if (!this.showTimetable) return 1;
    const param = qs.parse(this.props.location.search, {
      ignoreQueryPrefix: true,
    }).view;
    if (param === "liste") return 1;
    if (param === "stundenplan") return 2;
    if (this.restored && this.restored.state.view === 1) return 1;
    return 2;
  };

  handleViewChange = (view: number) => {
    // Leaving the Stundenplan keeps its range, day and data, so switching back
    // shows it without reloading.
    this.setState({ view, timetableState: this.currentTimetableState() });
    this.syncViewToUrl(view);
  };

  /**
   * The Stundenplan's state as it is on screen now. Called while it is still
   * mounted - also from componentWillUnmount, which React runs on this screen
   * before it unmounts the children.
   */
  currentTimetableState = (): TimetableSnapshot | null => {
    if (this.state.view === 2 && this.timetableRef.current) {
      return this.timetableRef.current.getSnapshot();
    }
    return this.state.timetableState;
  };

  /**
   * The view lives in the URL so that "Zurück" from a course lands on it and
   * the Stundenplan can be linked directly. replace, not push, like the tab
   * switch on the participants screen.
   */
  syncViewToUrl = (view: number) => {
    const params: any = qs.parse(this.props.location.search, {
      ignoreQueryPrefix: true,
    });
    // the Stundenplan is the default and needs no parameter
    const wanted = view === 1 ? "liste" : undefined;
    if (params.view === wanted) return;
    if (wanted) {
      params.view = wanted;
    } else {
      delete params.view;
    }
    this.props.history.replace({
      search: qs.stringify(params),
      hash: this.props.location.hash,
    });
  };

  /**
   * The cards navigate through their own <Link>s, so there is no single
   * redirect handler to hook. A capture-phase listener on the list container
   * catches all of them without touching the card components - and ignores
   * sidebar navigation, which happens outside this subtree.
   */
  handleListClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target && target.closest && target.closest("a")) {
      this.leavingToDetail = true;
    }
  };

  render() {
    if (!true) {
      return (
        <>
          <div className="loading-container">
            <HashLoader color={"#c31924"} size={100} loading={true} />
          </div>
        </>
      );
    }
    return (
      <>
        <div
          className="subscreen-main-container"
          ref={this.rootRef}
          onClickCapture={this.handleListClick}
        >
          <div className="subscreen-box-container">
            <div className="course-title-row">
              <Title title="Kurse" />
              {this.showTimetable && (
                <CourseViewSwitch
                  view={this.state.view}
                  onChange={this.handleViewChange}
                />
              )}
            </div>
            {this.state.view === 2 && (
              <Timetable
                ref={this.timetableRef}
                scope="club"
                clubId={this.state.clubId}
                initialState={this.state.timetableState}
              />
            )}
            {this.state.view === 1 && (
              <>
                <SearchInput
                  name="course-search-input"
                  placeholder="Kurssuche"
                  value={this.state.courseSearch}
                  onChange={this.handleChangeCourseSearch}
                  className="no-margin"
                />
                {this.state.courses.length === 0 && this.state.coursesLoaded && (
                  <>
                    {this.state.courseSearch !== "" ? (
                      <div className="no-courses-container">
                        Mit diesem Filter gibt es aktuell keine Kurse.
                      </div>
                    ) : (
                      <div className="no-courses-container">
                        Hier gibts es aktuell keine Kurse.
                      </div>
                    )}
                  </>
                )}

                <InfiniteScroll
                  dataLength={this.state.courses.length}
                  next={this.getNextPage}
                  hasMore={this.state.hasMore}
                  loader={<></>}
                  scrollableTarget="scroll-container"
                >
                  {this.state.courses.map((course: any, index: number) => (
                    <>
                      {course.courseType === "single-course" && (
                        <SingleCourseElementClub
                          clubId={this.state.clubId}
                          branchId={course.branchId}
                          courseId={course.id}
                          title={course.title}
                          description={course.description}
                          startTime={course.startTime}
                          endTime={course.endTime}
                          where={course.location.name}
                          freePlaces={
                            course.testmode
                              ? course.freePlacesTest
                              : course.freePlaces
                          }
                          price={priceToStr(course.price)}
                          imgUrl={course.imgLowRes}
                          key={course.id}
                          bookableFrom={firebaseDateToDateObj(course.bookableDate)}
                          userAllowedToBook={course.userAllowedToBook}
                          minAge={course.minAge}
                          maxAge={course.maxAge}
                        />
                      )}
                      {course.courseType === "block-course" && (
                        <BlockCourseElementClub
                          clubId={this.state.clubId}
                          branchId={course.branchId}
                          courseId={course.id}
                          title={course.title}
                          description={course.description}
                          when={dateToFullStr(
                            firebaseDateToDateObj(course.startTime)
                          )}
                          courses={course.courses}
                          freePlaces={
                            course.testmode
                              ? course.freePlacesTest
                              : course.freePlaces
                          }
                          price={priceToStr(course.price)}
                          imgUrl={course.imgLowRes}
                          key={course.id}
                          bookableFrom={firebaseDateToDateObj(course.bookableDate)}
                          userAllowedToBook={course.userAllowedToBook}
                          minAge={course.minAge}
                          maxAge={course.maxAge}
                        />
                      )}
                      {course.courseType === "infinity-course" && (
                        <InfinityCourseElementClub
                          clubId={this.state.clubId}
                          branchId={course.branchId}
                          courseId={course.id}
                          title={course.title}
                          description={course.description}
                          where={course.location?.name}
                          time={course.time}
                          duration={course.duration}
                          weekDay={course.weekDay}
                          freePlaces={
                            course.testmode
                              ? course.freePlacesTest
                              : course.freePlaces
                          }
                          price={priceToStr(course.price)}
                          imgUrl={course.imgLowRes}
                          key={course.id}
                          bookableFrom={firebaseDateToDateObj(course.bookableDate)}
                          userAllowedToBook={course.userAllowedToBook}
                          minAge={course.minAge}
                          maxAge={course.maxAge}
                        />
                      )}
                    </>
                  ))}
                </InfiniteScroll>
                {!this.state.coursesLoaded && (
                  <>
                    <CourseElementSkeleton />
                    <CourseElementSkeleton />
                    <CourseElementSkeleton />
                    <CourseElementSkeleton />
                  </>
                )}
                {this.state.coursesLoaded && this.state.hasMore && (
                  <div className="load-more-container" onClick={this.getNextPage}>
                    Mehr laden
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </>
    );
  }
}
